import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const SubsidiaryContext = createContext(null);

export const SubsidiaryProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false);

  // Fetch subsidiaries when user is authenticated - only once
  useEffect(() => {
    if (isAuthenticated && !dataFetched) {
      fetchSubsidiaries();
    } else if (!isAuthenticated) {
      setSubsidiaries([]);
      setSelectedSubsidiary(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch subsidiaries from API
  const fetchSubsidiaries = async () => {
    // Prevent multiple simultaneous fetches
    if (loading) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      if (!token) {
        setError('You need to log in again.');
        setLoading(false);
        return;
      }

      const response = await axios.get('/api/subsidiaries', {
        headers: { 'x-auth-token': token }
      });

      setSubsidiaries(response.data.subsidiaries || []);
      setDataFetched(true);

      // Restore previously selected subsidiary from localStorage
      if (response.data.subsidiaries && response.data.subsidiaries.length > 0) {
        const savedSubsidiaryId = localStorage.getItem('selectedSubsidiaryId');
        if (savedSubsidiaryId && savedSubsidiaryId !== 'null') {
          // Find the saved subsidiary in the list
          const savedSubsidiary = response.data.subsidiaries.find(
            s => String(s.id) === String(savedSubsidiaryId)
          );
          if (savedSubsidiary) {
            setSelectedSubsidiary(savedSubsidiary);
          } else {
            // Saved subsidiary not found, default to "All Subsidiaries" (null)
            setSelectedSubsidiary(null);
          }
        } else {
          // No saved subsidiary or explicitly null, show all
          setSelectedSubsidiary(null);
        }
      }

    } catch (error) {
      console.error('Error fetching subsidiaries:', error);
      if (error.response && error.response.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError('Failed to fetch subsidiaries. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Select a subsidiary (or null for "All Subsidiaries")
  const selectSubsidiary = (subsidiary) => {
    setSelectedSubsidiary(subsidiary);
    // Save to localStorage (null means "All Subsidiaries")
    if (subsidiary) {
      localStorage.setItem('selectedSubsidiaryId', subsidiary.id);
    } else {
      localStorage.setItem('selectedSubsidiaryId', 'null');
    }
  };

  // Create a new subsidiary
  const createSubsidiary = async (subsidiaryData) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/subsidiaries', subsidiaryData, {
        headers: { 'x-auth-token': token }
      });

      // POST /api/subsidiaries returns the row itself (not { subsidiary }); an
      // undefined entry here crashed the list render on logo_url (GlitchTip 2026-07-22).
      const created = response.data.subsidiary || response.data;

      // Add the new subsidiary to the list
      setSubsidiaries(prevSubsidiaries => [...prevSubsidiaries, created]);

      return { success: true, subsidiary: created };
    } catch (error) {
      console.error('Error creating subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to create subsidiary.');
      return { success: false, error: error.response?.data?.message || 'Creation failed' };
    } finally {
      setLoading(false);
    }
  };

  // Update a subsidiary
  const updateSubsidiary = async (subsidiaryId, subsidiaryData) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/subsidiaries/${subsidiaryId}`, subsidiaryData, {
        headers: { 'x-auth-token': token }
      });

      // PUT /api/subsidiaries/:id also returns the row itself (see create above).
      const updated = response.data.subsidiary || response.data;

      // Update the subsidiary in the list
      setSubsidiaries(prevSubsidiaries =>
        prevSubsidiaries.map(subsidiary =>
          subsidiary.id === subsidiaryId ? updated : subsidiary
        )
      );

      // Update selected subsidiary if it's the one being updated
      if (selectedSubsidiary && selectedSubsidiary.id === subsidiaryId) {
        setSelectedSubsidiary(updated);
      }

      return { success: true, subsidiary: updated };
    } catch (error) {
      console.error('Error updating subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to update subsidiary.');
      return { success: false, error: error.response?.data?.message || 'Update failed' };
    } finally {
      setLoading(false);
    }
  };

  // Delete a subsidiary
  const deleteSubsidiary = async (subsidiaryId) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      await axios.delete(`/api/subsidiaries/${subsidiaryId}`, {
        headers: { 'x-auth-token': token }
      });

      // Remove the subsidiary from the list
      const updatedSubsidiaries = subsidiaries.filter(subsidiary => subsidiary.id !== subsidiaryId);
      setSubsidiaries(updatedSubsidiaries);

      // If the deleted subsidiary was selected, reset to "All Subsidiaries"
      if (selectedSubsidiary && selectedSubsidiary.id === subsidiaryId) {
        setSelectedSubsidiary(null);
        localStorage.setItem('selectedSubsidiaryId', 'null');
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to delete subsidiary.');
      return { success: false, error: error.response?.data?.message || 'Deletion failed' };
    } finally {
      setLoading(false);
    }
  };

  // Link a brand to a subsidiary
  const linkBrand = async (subsidiaryId, brandId, relationshipData = {}) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/subsidiaries/${subsidiaryId}/brands`,
        { brandId, ...relationshipData },
        { headers: { 'x-auth-token': token } }
      );

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error linking brand to subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to link brand.');
      return { success: false, error: error.response?.data?.message || 'Linking failed' };
    } finally {
      setLoading(false);
    }
  };

  // Unlink a brand from a subsidiary
  const unlinkBrand = async (subsidiaryId, brandId) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      await axios.delete(`/api/subsidiaries/${subsidiaryId}/brands/${brandId}`, {
        headers: { 'x-auth-token': token }
      });

      return { success: true };
    } catch (error) {
      console.error('Error unlinking brand from subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to unlink brand.');
      return { success: false, error: error.response?.data?.message || 'Unlinking failed' };
    } finally {
      setLoading(false);
    }
  };

  // Assign a connector to a subsidiary
  const assignConnector = async (subsidiaryId, connectorData) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/subsidiaries/${subsidiaryId}/connectors`,
        connectorData,
        { headers: { 'x-auth-token': token } }
      );

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error assigning connector to subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to assign connector.');
      return { success: false, error: error.response?.data?.message || 'Assignment failed' };
    } finally {
      setLoading(false);
    }
  };

  // Unassign a connector from a subsidiary
  const unassignConnector = async (subsidiaryId, connectorId, profileIdentifier = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const url = profileIdentifier
        ? `/api/subsidiaries/${subsidiaryId}/connectors/${connectorId}?profileIdentifier=${profileIdentifier}`
        : `/api/subsidiaries/${subsidiaryId}/connectors/${connectorId}`;

      await axios.delete(url, {
        headers: { 'x-auth-token': token }
      });

      return { success: true };
    } catch (error) {
      console.error('Error unassigning connector from subsidiary:', error);
      setError(error.response?.data?.message || 'Failed to unassign connector.');
      return { success: false, error: error.response?.data?.message || 'Unassignment failed' };
    } finally {
      setLoading(false);
    }
  };

  // Discover profiles for a multi-profile connector
  const discoverProfiles = async (connectorId) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/subsidiaries/connectors/${connectorId}/discover-profiles`,
        {},
        { headers: { 'x-auth-token': token } }
      );

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error discovering profiles:', error);
      setError(error.response?.data?.message || 'Failed to discover profiles.');
      return { success: false, error: error.response?.data?.message || 'Discovery failed' };
    } finally {
      setLoading(false);
    }
  };

  // Get profile assignments for a connector
  const getProfileAssignments = async (connectorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/subsidiaries/connectors/${connectorId}/profile-assignments`,
        { headers: { 'x-auth-token': token } }
      );

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching profile assignments:', error);
      return { success: false, error: error.response?.data?.message || 'Fetch failed' };
    }
  };

  // Get brands linked to a subsidiary
  const getSubsidiaryBrands = async (subsidiaryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/subsidiaries/${subsidiaryId}/brands`, {
        headers: { 'x-auth-token': token }
      });

      // GET /:id/brands returns the rows as a bare array (controllers.js res.json(brands)).
      return { success: true, brands: Array.isArray(response.data) ? response.data : (response.data.brands || []) };
    } catch (error) {
      console.error('Error fetching subsidiary brands:', error);
      return { success: false, error: error.response?.data?.message || 'Fetch failed' };
    }
  };

  // Get connectors assigned to a subsidiary
  const getSubsidiaryConnectors = async (subsidiaryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/subsidiaries/${subsidiaryId}/connectors`, {
        headers: { 'x-auth-token': token }
      });

      // GET /:id/connectors returns the rows as a bare array (controllers.js res.json(connectors)).
      return { success: true, connectors: Array.isArray(response.data) ? response.data : (response.data.connectors || []) };
    } catch (error) {
      console.error('Error fetching subsidiary connectors:', error);
      return { success: false, error: error.response?.data?.message || 'Fetch failed' };
    }
  };

  // Force a refresh only if not already loading
  const manualRefresh = () => {
    if (!loading) {
      setDataFetched(false);
      fetchSubsidiaries();
    }
  };

  return (
    <SubsidiaryContext.Provider value={{
      subsidiaries,
      selectedSubsidiary,
      loading,
      error,
      selectSubsidiary,
      createSubsidiary,
      updateSubsidiary,
      deleteSubsidiary,
      linkBrand,
      unlinkBrand,
      assignConnector,
      unassignConnector,
      discoverProfiles,
      getProfileAssignments,
      getSubsidiaryBrands,
      getSubsidiaryConnectors,
      refreshSubsidiaries: manualRefresh
    }}>
      {children}
    </SubsidiaryContext.Provider>
  );
};

export const useSubsidiary = () => {
  const context = useContext(SubsidiaryContext);
  if (!context) {
    throw new Error('useSubsidiary must be used within a SubsidiaryProvider');
  }
  return context;
};

export default SubsidiaryContext;
